// SlipSync feedback panels for the Import sheet (#1226).
// Single job: render what a SlipSync run is doing (progress) and what it did (result),
// from values useSlipSync already owns. Presentational only: no hook, no data, no
// navigation; the sheet passes the book, the counts and the two callbacks.
// Does NOT render hard failures (login expired, rate limited, network): the sheet keeps
// its existing error message for those.
import { JT } from "./jumbotronTokens";

const MAX_SKELETONS = 3;
const plural = (n, one, many) => (n === 1 ? one : many);

const eyebrow = { ...JT.type.label, display: "flex", alignItems: "center", gap: 8 };
const headline = { ...JT.display800, fontSize: 26, lineHeight: 1.05, textTransform: "uppercase", letterSpacing: ".3px", color: JT.text.primary, margin: 0 };
const subline = { fontSize: 13, color: JT.text.secondary, margin: 0 };
const note = { fontSize: 12, color: JT.text.secondary, background: JT.bg.well, border: `1px solid ${JT.border.default}`, borderRadius: JT.radius.insetRow, padding: "10px 12px" };
const button = { flex: 1, minHeight: 44, borderRadius: 12, fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer" };

function BookEyebrow({ book }) {
  const color = (book && JT.book[book.key]) || JT.brand.accent;
  return (
    <div style={{ ...eyebrow, color }}>
      <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
      {book ? book.displayName : "SlipSync"}
    </div>
  );
}

export function SyncProgressPanel({ book, foundCount, capture = false }) {
  // #885 capture stage: the book's responses are being saved; no bets are being added.
  if (capture) {
    return (
      <div role="status" aria-live="polite" aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <BookEyebrow book={book} />
        <p style={headline}>Saving your {book ? book.displayName : "sportsbook"} capture</p>
        <p style={subline}>Nothing is imported yet.</p>
      </div>
    );
  }
  const found = Number.isInteger(foundCount) && foundCount > 0 ? foundCount : null;
  const cards = Math.min(found || 1, MAX_SKELETONS);
  return (
    <div role="status" aria-live="polite" aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <BookEyebrow book={book} />
      <p style={headline}>Syncing your bets</p>
      <p style={subline}>
        {found ? `Found ${found} ${plural(found, "bet", "bets")}. Adding them to SlipTrack.` : "Adding your bets to SlipTrack."}
      </p>
      <div>
        {Array.from({ length: cards }, (_, i) => (
          <div key={i} data-testid="sync-skeleton" className="skeleton" style={{ height: 64, borderRadius: JT.radius.tile, marginBottom: 10, opacity: 0.5 }} />
        ))}
      </div>
    </div>
  );
}

function CountRow({ label, value, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "11px 14px", fontSize: 13, color: JT.text.secondary }}>
      {label}
      <b style={{ fontSize: 15, fontVariantNumeric: "tabular-nums", color }}>{value}</b>
    </div>
  );
}

export function SyncResultPanel({ book, summary, onDone, onAddScreenshot }) {
  const bookName = book ? book.displayName : "This book";
  // #885 capture stage: raw book responses were stored; nothing was imported.
  if (summary && summary.unknown && summary.capture) {
    return (
      <ResultShell book={book} onDone={onDone}>
        <p style={headline}>Capture sent</p>
        <p style={subline}>{bookName} sync is on its way.</p>
      </ResultShell>
    );
  }
  if (summary && Number.isInteger(summary.captured) && summary.captured >= 0) {
    return (
      <ResultShell book={book} onDone={onDone}>
        <p style={headline}>Thanks, we captured {summary.captured} {plural(summary.captured, "page", "pages")}</p>
        <p style={subline}>{bookName} sync is on its way. Nothing was imported yet.</p>
      </ResultShell>
    );
  }
  if (!summary || summary.unknown) {
    return (
      <ResultShell book={book} onDone={onDone}>
        <p style={headline}>Sync finished</p>
        <p style={subline}>Check Bets and History for your bets.</p>
      </ResultShell>
    );
  }
  const imported = Number(summary.imported) || 0;
  const skipped = Number(summary.skipped) || 0;
  const failed = Number(summary.failed) || 0;
  const untracked = Number(summary.untracked) || 0;

  if (imported === 0 && failed === 0) {
    return (
      <ResultShell book={book} onDone={onDone}>
        <p style={headline}>You're up to date</p>
        <p style={subline}>
          {skipped > 0 ? `No new bets. ${skipped === 1 ? "It's" : `All ${skipped} are`} already in SlipTrack.` : "No new bets."}
        </p>
      </ResultShell>
    );
  }

  const title = failed === 0
    ? `${imported} new ${plural(imported, "bet", "bets")} synced`
    : imported > 0
      ? `${imported} of ${imported + failed} bets synced`
      : `Couldn't import ${failed} ${plural(failed, "bet", "bets")}`;

  return (
    <ResultShell book={book} onDone={onDone} onAddScreenshot={failed > 0 ? onAddScreenshot : null}>
      <p style={headline}>{title}</p>
      {(imported > 0 || skipped > 0) && <div style={{ display: "flex", flexDirection: "column", background: JT.bg.tile, border: `1px solid ${JT.border.default}`, borderRadius: JT.radius.tile }}>
        {imported > 0 && <CountRow label="New" value={imported} color={JT.status.won.color} />}
        {skipped > 0 && <CountRow label="Already in SlipTrack" value={skipped} color={JT.text.primary} />}
        {failed > 0 && imported > 0 && <CountRow label="Couldn't import" value={failed} color={JT.status.missing.color} />}
      </div>}
      {imported > 0 && (
        <div style={note}>
          Open bets show on <strong style={{ color: JT.text.primary }}>Bets</strong>. Bets that already finished go to <strong style={{ color: JT.text.primary }}>History</strong>.
        </div>
      )}
      {imported > 0 && untracked > 0 && (
        <div style={note}>
          {untracked} {plural(untracked, "leg", "legs")} couldn't be tracked yet: we've logged it and we'll add support.
        </div>
      )}
      {failed > 0 && (
        <div style={{ ...note, color: JT.status.missing.color, background: JT.status.missing.dim, borderColor: JT.status.missing.border }}>
          SlipTrack couldn't read {failed === 1 ? "one bet" : `${failed} bets`} from {book ? book.displayName : "your sportsbook"} yet. You can still add {plural(failed, "it", "them")} from a screenshot.
        </div>
      )}
    </ResultShell>
  );
}

function ResultShell({ book, children, onDone, onAddScreenshot }) {
  return (
    <div role="status" aria-live="polite" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <BookEyebrow book={book} />
      {children}
      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <button onClick={onDone} style={{ ...button, background: onAddScreenshot ? "none" : JT.brand.cta, border: onAddScreenshot ? `1px solid ${JT.border.mid}` : "none", color: onAddScreenshot ? JT.text.secondary : JT.text.primary }}>
          Done
        </button>
        {onAddScreenshot && (
          <button onClick={onAddScreenshot} style={{ ...button, background: JT.brand.cta, border: "none", color: JT.text.primary }}>
            Add screenshot
          </button>
        )}
      </div>
    </div>
  );
}
